import type { ConfigFieldKey, RiskModelType } from "@unbox/api";
import { MERCHANT_RISK_CONFIG_FIELDS } from "@unbox/api";
import { MERCHANT_MANAGED_PLACEHOLDERS, placeholderKeysFor } from "./keyword-config";

// A risk is composed of parts (its per-merchant config fields, plus any
// {variable} tokens in its detection prompt), and each part has one editor:
// - "customer": edited by the merchant in the app; the merchant-facing save
//   endpoint accepts only these fields.
// - "console": edited here in the admin console by the Unbox safety team; the
//   merchant endpoint rejects these fields.
// - "engineering": not changeable by editing config — changes are code changes
//   in the pipeline. `omni_risk_name` only maps a catalog row onto one of the
//   omni model's fixed moderation categories; remapping it is an engineering
//   decision, not detection config.
// Everything else on a merchant risk — on/off, severity, auto-approve, response
// policy, creator explanation, domain knowledge values — is always
// customer-editable in the app, whatever the model type.
export type ComponentEditor = "customer" | "console" | "engineering";

export const CONFIG_FIELD_EDITOR: Record<ConfigFieldKey, ComponentEditor> = {
  banned_keywords: "customer",
  approved_claims: "customer",
  expected_strings: "customer",
  description: "console",
  hardcoded_rules: "console",
  system_prompt: "console",
  omni_risk_name: "engineering",
  threshold: "console",
  confidence_threshold: "console",
};

export type RiskComponent = { label: string; editor: ComponentEditor; tooltip: string };

// One component per editor group, not per field, so a row shows at most one
// console badge and one customer badge.
const GROUP_LABELS: Partial<Record<ConfigFieldKey, string>> = {
  banned_keywords: "Banned keywords",
  approved_claims: "Approved claims",
  expected_strings: "Expected strings",
  threshold: "Tolerance",
};

const groupLabel = (keys: ConfigFieldKey[]): string =>
  keys.length === 1 ? (GROUP_LABELS[keys[0]] ?? keys[0]) : "Detection";

const CONSOLE_GROUP_TOOLTIPS: Record<string, string> = {
  Tolerance:
    "What this risk detects is fixed — one of the omni model's moderation categories. Only the tolerance (score threshold) is editable, here in the console, per merchant.",
  Detection: "What this risk detects (the prompt and rules) is edited here in the console by the Unbox safety team.",
};

// The parts a risk is composed of, given its model type and effective merchant
// config. An empty list means nothing is editable — changes need engineering.
export const riskComponentsFor = (modelType: RiskModelType, config: Record<string, unknown>): RiskComponent[] => {
  const components: RiskComponent[] = [];
  const fieldKeys = MERCHANT_RISK_CONFIG_FIELDS[modelType].map((f) => f.key);
  const consoleKeys = fieldKeys.filter((key) => CONFIG_FIELD_EDITOR[key] === "console");
  const customerKeys = fieldKeys.filter((key) => CONFIG_FIELD_EDITOR[key] === "customer");

  if (consoleKeys.length > 0) {
    const label = groupLabel(consoleKeys);
    components.push({
      label,
      editor: "console",
      tooltip: CONSOLE_GROUP_TOOLTIPS[label] ?? "Edited here in the console by the Unbox safety team.",
    });
  }
  if (customerKeys.length > 0) {
    components.push({
      label: groupLabel(customerKeys),
      editor: "customer",
      tooltip: "Edited by the customer in the app.",
    });
  }

  // {variable} tokens in the detection prompt render as editable fields in the
  // customer app — the values (not the logic) are customer-editable. System-
  // resolved and merchant-details-managed placeholders are excluded because the
  // customer app doesn't render them as editable fields here.
  const variableKeys = placeholderKeysFor(modelType, config).filter((key) => !MERCHANT_MANAGED_PLACEHOLDERS.has(key));
  if (variableKeys.length > 0) {
    components.push({
      label: "Variables",
      editor: "customer",
      tooltip: `Variables in this risk's detection prompt (${variableKeys.map((key) => `{${key}}`).join(", ")}) appear as editable fields in the customer app. The detection logic itself is not customer-editable.`,
    });
  }

  return components;
};

export const ENGINEERING_ONLY_BADGE = {
  label: "Engineering only",
  tooltip: "This risk has no editable configuration — any change requires engineering.",
};
